import { useEffect } from 'react';
import { useSetState } from 'minimal-shared/hooks';

import { shortUUID } from 'src/utils/uuid';
import { getAverageRating } from 'src/utils/helper';

import { useTranslate } from 'src/locales';
import {
  getData,
  increment,
  updateData,
  deleteData,
  createBatch,
  serverTimestamp,
} from 'src/lib/firebase';

import { toast } from 'src/components/snackbar';
import { useReCaptcha } from 'src/components/recaptcha';

// ----------------------------------------------------------------------

const defaultRatings = [
  { name: 5, starCount: 0 },
  { name: 4, starCount: 0 },
  { name: 3, starCount: 0 },
  { name: 2, starCount: 0 },
  { name: 1, starCount: 0 },
];

const getRatingsData = async (id) => {
  const ratingsData = await getData({
    path: `ratings/${id}`,
    formatter: (data) => ({
      ...data,
      ratings: [
        { name: 5, starCount: data?.rating_5 ?? 0 },
        { name: 4, starCount: data?.rating_4 ?? 0 },
        { name: 3, starCount: data?.rating_3 ?? 0 },
        { name: 2, starCount: data?.rating_2 ?? 0 },
        { name: 1, starCount: data?.rating_1 ?? 0 },
      ],
    }),
  });

  const { totalRating, totalReviews } = getAverageRating(ratingsData);

  return { totalRating, totalReviews, ratings: ratingsData.ratings ?? defaultRatings };
};

export const LIMIT = 10;

export function useGetReviews(id, collection, openReviews = true) {
  const { t } = useTranslate();

  const verifyCaptcha = useReCaptcha();

  const { state, setState } = useSetState({
    reviews: [],
    loading: true,
    totalRating: 0,
    totalReviews: 0,
    loadingMore: true,
    lastVisible: null,
    ratings: defaultRatings,
  });

  const getSummary = async () => {
    const { ratings, totalRating, totalReviews } = await getRatingsData(id);
    setState({ ratings, totalRating, totalReviews });
  };

  const getReviews = async (prevReviews = [], lastVisible = null) => {
    const reviews = await getData({
      path: `ratings/${id}/reviews`,
      defaultData: [],
      options: {
        limit: LIMIT,
        startAfter: lastVisible,
        orderBy: [{ field: 'postedAt', value: 'desc' }],
      },
      formatter: (review) => ({
        rating: review.rating,
        comment: review.comment,
        photoURL: review.photoURL,
        displayName: review.displayName,
        isPurchased: !!review.isPurchased,
        postedAt: review.postedAt.toDate(),
        totalLikes: review.totalLikes ?? 0,
        attachments: review.attachments ?? [],
        totalDislikes: review.totalDislikes ?? 0,
      }),
    });

    setState({
      loading: false,
      loadingMore: false,
      reviews: [...prevReviews, ...reviews],
      lastVisible: reviews[reviews.length - 1],
    });
  };

  const loadMoreReviews = () => {
    setState({ loadingMore: true });
    getReviews(state.reviews, state.lastVisible);
  };

  const addNewReview = async (newReview) => {
    const currentState = state;
    const reviewId = shortUUID();
    setState({
      totalReviews: currentState.totalReviews + 1,
      reviews: [{ ...newReview, id: reviewId }, ...currentState.reviews],
    });
    try {
      const isValid = await verifyCaptcha();
      if (!isValid) throw new Error('Captcha detected the use of robots');
      const batch = createBatch();
      updateData(
        `ratings/${id}/reviews/${reviewId}`,
        { ...newReview, postedAt: serverTimestamp() },
        batch
      );
      updateData(`ratings/${id}`, { [`rating_${newReview.rating}`]: increment(1) }, batch);
      await batch.commit();
      const { ratings, totalRating, totalReviews } = await getRatingsData(id);
      await updateData(`${collection}/${id}`, { totalRating, totalReviews: increment(1) });
      setState({
        ratings,
        totalRating,
        totalReviews,
        reviews: [{ ...newReview, id: reviewId, postedAt: new Date() }, ...currentState.reviews],
      });
      toast.success(t('Thanks for your rating'));
    } catch (error) {
      console.error(error);
      toast.error(t('An error occurred, please try again'));
      setState(currentState);
    }
  };

  const removeReview = async (review) => {
    const currentState = state;
    setState({
      totalReviews: currentState.totalReviews - 1,
      reviews: currentState.reviews.filter((_review) => _review.id !== review.id),
    });
    try {
      const batch = createBatch();
      deleteData(`ratings/${id}/reviews/${review.id}`, batch);
      updateData(`ratings/${id}`, { [`rating_${review.rating}`]: increment(-1) }, batch);
      await batch.commit();
      const { ratings, totalRating, totalReviews } = await getRatingsData(id);
      await updateData(`${collection}/${id}`, { totalRating, totalReviews: increment(-1) });
      setState({ ratings, totalRating, totalReviews });
      toast.success(t('Rating removed'));
    } catch (error) {
      console.error(error);
      toast.error(t('An error occurred, please try again'));
      setState(currentState);
    }
  };

  const reportReview = async (review) => {
    try {
      const isValid = await verifyCaptcha();
      if (!isValid) throw new Error('Captcha detected the use of robots');
      await updateData(`reports/${review.id}`, {
        comment: review.comment,
        totalReports: increment(1),
        reportedAt: serverTimestamp(),
        isPurchased: !!review.isPurchased,
        attachments: review.attachments ?? [],
        path: `ratings/${id}/reviews/${review.id}`,
      });
      toast.success(t('Rating reported'));
    } catch (error) {
      console.error(error);
      toast.error(t('An error occurred, please try again'));
    }
  };

  useEffect(() => {
    if (id && openReviews) {
      getSummary();
      getReviews();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, openReviews]);

  return { ...state, addNewReview, removeReview, reportReview, loadMoreReviews };
}
